import React from 'react';

import { cn } from '@cineamo/ui/src/helpers/cn';

export type CalendarBarItemProps = {
    id?: string;
    className?: string;
    isItemActive?: boolean;
    isItemSelected?: boolean;
    isAlwaysHighlighted?: boolean;
    onItemClick?: () => void;
    dateItemWidth?: `${'!' | ''}w-${string}`;
    calendarItemStyle?: CalendarBarItemStyle | CalendarItemStyleCSSProperties;
    title?: string;
    subtitle?: string;
    customView?: React.JSX.Element;
    isDisabled?: boolean;
    isScrollTarget?: boolean;
    accessibilityLabel?: string;
};

export enum CalendarBarItemStyle {
    Default,
    On_white
}

export type CalendarItemStyleCSSProperties = {
    backgroundColor?: string;
    backgroundColorItemActive?: string;
    textColor?: string;
    textColorItemActive?: string;
};

export default function CalendarBarItem(props: CalendarBarItemProps): React.JSX.Element {
    const {
        id,
        className,
        title,
        subtitle,
        customView,
        isItemActive,
        isItemSelected,
        onItemClick,
        calendarItemStyle,
        dateItemWidth,
        isDisabled,
        isScrollTarget,
        accessibilityLabel
    } = props;

    let textColor: string,
        textColorSelected: string,
        textColorDisabled: string,
        backgroundColor: string,
        backgroundSelected: string,
        backgroundDisabled: string;

    switch (calendarItemStyle) {
        case CalendarBarItemStyle.On_white:
            textColor = 'text-darkBlue';
            textColorSelected = 'text-darkBlue';
            textColorDisabled = 'text-gray-middle';
            backgroundColor = 'bg-gray-light';
            backgroundSelected = 'bg-cyan-middle';
            backgroundDisabled = 'bg-gray-light';
            break;
        default:
            textColor = 'text-white';
            textColorSelected = 'text-darkBlue';
            textColorDisabled = 'text-white';
            backgroundColor = 'bg-white/10 hover:bg-white/20';
            backgroundSelected = 'bg-cyan';
            backgroundDisabled = 'bg-gray-light';
            break;
    }

    return (
        <button
            id={`__calendar_bar_item__${id || ''}`}
            aria-label={accessibilityLabel}
            aria-current={isItemSelected ? 'date' : undefined}
            className={cn(
                className,
                'relative',
                'h-56 rounded-4',
                'text-body-bold-10 whitespace-pre-wrap',
                'flex flex-col flex-none justify-center items-center',
                'transition-all duration-100',
                'select-none',
                isItemSelected ? '__selected__calendar__bar__date' : undefined,
                isScrollTarget ? '__scroll__target__calendar__bar__date' : undefined,
                isItemActive ? `${backgroundSelected} ${textColorSelected}` : `${textColor} ${backgroundColor}`,
                {
                    ['cursor-pointer']: !isDisabled,
                    [`${backgroundDisabled} ${textColorDisabled}`]: isDisabled && !isItemActive
                },
                dateItemWidth
            )}
            style={
                typeof calendarItemStyle === 'object'
                    ? {
                          backgroundColor: isItemActive
                              ? calendarItemStyle.backgroundColorItemActive
                              : calendarItemStyle.backgroundColor,
                          color: isItemActive ? calendarItemStyle.textColorItemActive : calendarItemStyle.textColor
                      }
                    : undefined
            }
            disabled={isDisabled}
            onClick={isDisabled ? undefined : onItemClick}>
            {title && <span>{title}</span>}
            {subtitle && <span>{subtitle}</span>}
            {customView || null}
        </button>
    );
}
